import { useEffect, useRef, useState } from "react";
import { getApiUrl } from "../api";
import { getStoredToken } from "../auth";

const initialFinancial = {
  monthlysalary: "",
  additionalincome: "",
  governmentsupport: "",
  pension: "",
  rentamount: "",
  electricity: "",
  water: "",
  internet: "",
  transportation: "",
  schoolfee: "",
  household: "",
  bankloan: "",
  credicards: "",
  outstandingpayment: "",
  iban: "",
  bankname: "",
  accountholder: "",
  employername: "",
  employeraddress: "",
  hrcontact: "",
};

const moneyFields = [
  ["monthlysalary", "Monthly Salary"],
  ["additionalincome", "Additional Income"],
  ["governmentsupport", "Government Support"],
  ["pension", "Pension"],
];

const expenseFields = [
  ["rentamount", "Monthly Rental Amount"],
  ["electricity", "Electricity"],
  ["water", "Water"],
  ["internet", "Internet"],
  ["transportation", "Transportation"],
  ["schoolfee", "School Fee"],
  ["household", "Household"],
];

const debtFields = [
  ["bankloan", "Monthly Bank Loan"],
  ["credicards", "Monthly Credit Card Installment"],
  ["outstandingpayment", "Outstanding Payment"],
];

function MoneyInput({ label, value, onChange, isArabic = false, required = false }) {
  return (
    <div className="bn-profile-card">
      <span>{label}</span>
      <input type="number" min="0" step="1" value={value ?? ""} onChange={onChange} placeholder={isArabic ? "درهم" : "AED"} required={required} />
    </div>
  );
}

export default function BeneficiaryFinancialInfoForm({ language = "en", requireAll = false, onSaved }) {
  const isArabic = language === "ar";
  const t = (english, arabic) => isArabic ? arabic : english;
  const fieldLabels = {
    "Monthly Salary": "الراتب الشهري", "Additional Income": "دخل إضافي", "Government Support": "الدعم الحكومي", Pension: "المعاش",
    "Monthly Rental Amount": "قيمة الإيجار الشهري", Electricity: "الكهرباء", Water: "المياه", Internet: "الإنترنت", Transportation: "المواصلات", "School Fee": "الرسوم الدراسية", Household: "المصروفات المنزلية",
    "Monthly Bank Loan": "القرض البنكي الشهري", "Monthly Credit Card Installment": "القسط الشهري لبطاقة الائتمان", "Outstanding Payment": "دفعات مستحقة",
  };
  const formRef = useRef(null);
  const [financial, setFinancial] = useState(initialFinancial);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const token = getStoredToken();

  const scrollToFormTop = () => {
    window.requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const response = await fetch(getApiUrl("/api/BeneficiaryFinancialInfo.ashx"), {
          credentials: "include",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load financial information.");
        }
        setFinancial({ ...initialFinancial, ...(result.financial || {}) });
      } catch (error) {
        if (error.name !== "AbortError") {
          setIsError(true);
          setMessage(error.message || "Unable to load financial information.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [token]);

  const updateField = (field) => (event) => {
    setFinancial((current) => ({ ...current, [field]: event.target.value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setIsError(false);
    setMessage("");
    try {
      const response = await fetch(getApiUrl("/api/BeneficiaryFinancialInfo.ashx"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(financial),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to save financial information.");
      }
      setFinancial({ ...initialFinancial, ...(result.financial || {}) });
      onSaved?.({ financial: result.financial || financial });
      setMessage(result.message || "Financial information saved successfully.");
      scrollToFormTop();
    } catch (error) {
      setIsError(true);
      setMessage(error.message || "Unable to save financial information.");
      scrollToFormTop();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="family-details-loading">{t("Loading financial information...", "جارٍ تحميل المعلومات المالية...")}</div>;

  return (
    <form ref={formRef} className="beneficiary-financial-form" onSubmit={save}>
      {message ? (
        <div className={`beneficiary-family-message ${isError ? "error" : "success"}`} role="alert">
          {message}
        </div>
      ) : null}

      <div className="bn-profile-wrap">
        <div className="bn-profile-box">
          <div className="bn-profile-section">
            <div className="bn-profile-title">
              <div className="bn-profile-title-icon"><i className="fa-solid fa-money-bill-trend-up"></i></div>
              <h4>{t("Income", "الدخل")}</h4>
            </div>
            <div className="bn-profile-grid">
              {moneyFields.map(([field, label]) => (
                <MoneyInput key={field} label={isArabic ? fieldLabels[label] : label} value={financial[field]} onChange={updateField(field)} isArabic={isArabic} required={requireAll} />
              ))}
            </div>
          </div>

          <div className="bn-profile-section">
            <div className="bn-profile-title">
              <div className="bn-profile-title-icon"><i className="fa-solid fa-wallet"></i></div>
              <h4>{t("Expenses", "المصروفات")}</h4>
            </div>
            <div className="bn-profile-grid">
              {expenseFields.map(([field, label]) => (
                <MoneyInput key={field} label={isArabic ? fieldLabels[label] : label} value={financial[field]} onChange={updateField(field)} isArabic={isArabic} required={requireAll} />
              ))}
            </div>
          </div>

          <div className="bn-profile-section">
            <div className="bn-profile-title">
              <div className="bn-profile-title-icon"><i className="fa-solid fa-file-invoice-dollar"></i></div>
              <h4>{t("Loans & Debts", "القروض والالتزامات")}</h4>
            </div>
            <div className="bn-profile-grid">
              {debtFields.map(([field, label]) => (
                <MoneyInput key={field} label={isArabic ? fieldLabels[label] : label} value={financial[field]} onChange={updateField(field)} isArabic={isArabic} required={requireAll} />
              ))}
            </div>
          </div>

          <div className="bn-profile-section">
            <div className="bn-profile-title">
              <div className="bn-profile-title-icon"><i className="fa-solid fa-building-columns"></i></div>
              <h4>{t("Bank Information", "المعلومات البنكية")}</h4>
            </div>
            <div className="bn-profile-grid">
              <div className="bn-profile-card"><span>IBAN</span><input value={financial.iban} onChange={updateField("iban")} maxLength="250" required={requireAll} /></div>
              <div className="bn-profile-card"><span>{t("Bank Name", "اسم البنك")}</span><input value={financial.bankname} onChange={updateField("bankname")} maxLength="250" required={requireAll} /></div>
              <div className="bn-profile-card"><span>{t("Account Holder", "صاحب الحساب")}</span><input value={financial.accountholder} onChange={updateField("accountholder")} maxLength="250" required={requireAll} /></div>
            </div>
          </div>

          <div className="bn-profile-section">
            <div className="bn-profile-title">
              <div className="bn-profile-title-icon"><i className="fa-solid fa-briefcase"></i></div>
              <h4>{t("Employer Information", "معلومات جهة العمل")}</h4>
            </div>
            <div className="bn-profile-grid">
              <div className="bn-profile-card"><span>{t("Employer Name", "اسم جهة العمل")}</span><input value={financial.employername} onChange={updateField("employername")} maxLength="4000" required={requireAll} /></div>
              <div className="bn-profile-card"><span>{t("Employer Address", "عنوان جهة العمل")}</span><input value={financial.employeraddress} onChange={updateField("employeraddress")} maxLength="250" required={requireAll} /></div>
              <div className="bn-profile-card"><span>{t("HR Contact", "جهة اتصال الموارد البشرية")}</span><input value={financial.hrcontact} onChange={updateField("hrcontact")} maxLength="250" required={requireAll} /></div>
            </div>
          </div>
        </div>
      </div>

      <button className="submit-btn" type="submit" disabled={isSaving}>
        {isSaving ? t("Saving...", "جارٍ الحفظ...") : t("Save Financial Info", "حفظ المعلومات المالية")}
      </button>
    </form>
  );
}
